/**
 * Service Worker bootloader code
 *
 * This scripts is injected into every pages.
 * It uses a few code structure hacks to reduce it's size.
 *
 * When editing, the output size should be compared to the previous size.
 *
 * Last registered code size was: 476 bytes
 */

import { readerPath } from "../env/baseUrl";
import { global } from "../env/global";

// keep the usage close so the url const is hoisted at function call
import ServiceWorkerUrl from "../../src/sw?worker&url";
const url = global.origin + readerPath(ServiceWorkerUrl);

const scope = readerPath("/");
const container: ServiceWorkerContainer = navigator.serviceWorker;

// this function will be eliminated in normal builds
// see: https://github.com/vitejs/vite/issues/10886
function perfWarn(state: string): void {
  if (!import.meta.env.DEBUG_LOAD_TIME) return;
  console.warn(`[${performance.now()}] SW ${state} from ${import.meta.url}`);
}

const mark = <TName extends string>(name: TName): TName => (
  perfWarn(name),
  performance.mark(
    `${import.meta.env.BOOTLOADER ? "boot" : "main"}-sw-${name}`
  ),
  name
);

export type SWRegistrationResult = "existing" | "registered";
export type SWRegistration = {
  readonly url: string;
  readonly scope: string;
  get(
    state: (result: SWRegistrationResult) => void
  ): Promise<ServiceWorkerRegistration>;
};

const register = (): Promise<ServiceWorkerRegistration> =>
  (mark("registering"),
  container.register(url, {
    type: import.meta.env.DEV ? "module" : "classic",
    scope,
  })).then((reg) => ((result = mark("registered")), reg));

declare global {
  interface CustomGlobalConstants {
    _DM_SW_REG?: SWRegistration;
  }
}

let result: SWRegistrationResult | undefined;
let loading: Promise<ServiceWorkerRegistration> | undefined;
export const Registration = (global._DM_SW_REG ||=
  (mark("start"),
  (loading = container.getRegistration(scope).then(
    (reg) =>
      // check if the existing registration matches the current SW URL
      ((reg?.active || reg?.waiting || reg?.installing)?.scriptURL === url &&
        // mark as existing and return the registration
        ((result = mark("existing")), reg)) ||
      // no existing registration, proceed to register a new one
      register()
  )),
  // prevent unhandled promise rejection
  // actual promise will be consumed by the main code
  loading.catch((_) => _),
  // actual SWRegistration exported
  {
    url,
    scope,

    // the first call consumes the eager attempt started below,
    // each subsequent call starts a fresh registration
    get: (
      state: (result: SWRegistrationResult) => void
    ): Promise<ServiceWorkerRegistration> =>
      (loading ||= register())
        .finally((_: void) => (loading = _ as undefined))
        .then((reg) => (state(result!), reg)),
  }));
